import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaCalendarAlt, FaChevronDown, FaExclamationTriangle } from "react-icons/fa";

import {
  RESELL_BOXES,
  findCategory,
  summarizeBox,
  getInitials,
  colorFromString,
  resolveStatusBadge,
} from "./engagementUtils";

/* ────────────────────────── Key/value list ────────────────────────── */

const KeyValueList = ({ items }) => {
  if (!items || !items.length) {
    return (
      <div style={{ fontSize: 12.5, color: "var(--color-text-light)", fontStyle: "italic" }}>
        No data available for this section.
      </div>
    );
  }
  return (
    <div>
      {items.map((it, idx) => (
        <div
          key={idx}
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            padding: "8px 0",
            borderBottom:
              idx === items.length - 1 ? "none" : "1px solid var(--color-border)",
          }}
        >
          <span
            style={{
              minWidth: 150,
              flexShrink: 0,
              fontSize: 12.5,
              fontWeight: 500,
              color: "var(--color-text-light)",
            }}
          >
            {it.name || "—"}
          </span>
          <span
            style={{
              flex: 1,
              minWidth: 180,
              fontSize: 13,
              fontWeight: 600,
              color: "var(--color-text)",
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {it.value ?? "N/A"}
            {it.low_confidence && (
              <span
                title="Low-confidence extraction — verify against source PDF"
                style={{ color: "var(--color-warning, #b45309)", display: "inline-flex" }}
              >
                <FaExclamationTriangle size={11} />
              </span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
};

/* ────────────────────────── Collapsible section row ────────────────────────── */

const CollapsibleSection = ({ icon: Icon, accent, label, count, unit, open, onToggle, children }) => (
  <div
    style={{
      border: "1px solid var(--color-border)",
      borderRadius: 10,
      background: "var(--color-bg)",
      marginBottom: 8,
      overflow: "hidden",
    }}
  >
    <button
      type="button"
      onClick={onToggle}
      style={{
        all: "unset",
        boxSizing: "border-box",
        width: "100%",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "11px 14px",
      }}
    >
      <span
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: `${accent}1f`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={14} color={accent} />
      </span>
      <span
        style={{
          fontSize: 13.5,
          fontWeight: 600,
          color: "var(--color-text)",
          flex: 1,
          minWidth: 0,
        }}
      >
        {label}
      </span>
      <motion.span
        animate={{ rotate: open ? 180 : 0 }}
        transition={{ duration: 0.2 }}
        style={{ display: "flex", color: "var(--color-text-light)" }}
      >
        <FaChevronDown size={12} />
      </motion.span>
    </button>

    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.22 }}
          style={{ overflow: "hidden" }}
        >
          <div style={{ padding: "4px 14px 14px" }}>{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

/* ────────────────────────── Single resell deal card ────────────────────────── */

const ResellCard = ({ deal, index }) => {
  const status = resolveStatusBadge(deal);
  const pmoEmail = deal.assigned_to || null;
  const initials = getInitials(pmoEmail || deal.deal_name);
  const avatarColor = colorFromString(pmoEmail || deal.deal_name || "");

  // Whole-card collapse: lower details hidden by default, slide open on header click.
  const [cardOpen, setCardOpen] = useState(false);

  const [openSections, setOpenSections] = useState({});
  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  // Latest update display (resell deals use "categories" only — surface a status line)
  const dealStatusItem = (deal.categories || [])
    .flatMap((c) => c.items || [])
    .find((it) => /status/i.test(it.name || "") || /milestone|update/i.test(it.name || ""));

  const cardAnim = {
    initial: { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0 },
  };

  return (
    <motion.div
      variants={cardAnim}
      initial="initial"
      animate="animate"
      transition={{ delay: index * 0.06 }}
      style={{
        background: "var(--color-bg-card)",
        border: "1px solid var(--color-border)",
        borderRadius: 14,
        padding: 20,
        marginBottom: 16,
        boxShadow: "var(--shadow-sm)",
        borderLeft: `4px solid #f59e0b`,
      }}
    >
      {/* Header: title + service areas + status + chevron (whole row toggles the card) */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={cardOpen}
        onClick={() => setCardOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setCardOpen((o) => !o);
          }
        }}
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: cardOpen ? 16 : 0,
          cursor: "pointer",
          userSelect: "none",
          transition: "margin-bottom 0.2s ease",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
            minWidth: 0,
            flex: 1,
          }}
        >
          <h5
            title={deal.deal_name}
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: "var(--color-text)",
              margin: 0,
              wordBreak: "break-word",
            }}
          >
            {deal.deal_name}
          </h5>

          {deal.service_areas?.map((s, k) => (
            <span
              key={k}
              style={{
                fontSize: 12,
                padding: "3px 10px",
                borderRadius: 999,
                background: "var(--color-bg)",
                border: "1px solid var(--color-border)",
                color: "var(--color-text-light)",
                fontWeight: 500,
              }}
            >
              {s}
            </span>
          ))}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 10px",
              borderRadius: 999,
              background: status.bg,
              color: status.color,
              fontWeight: 600,
              fontSize: 12,
              whiteSpace: "nowrap",
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: status.color,
              }}
            />
            {status.label}
          </span>

          <motion.span
            animate={{ rotate: cardOpen ? 180 : 0 }}
            transition={{ duration: 0.25 }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 26,
              height: 26,
              borderRadius: 8,
              background: "var(--color-bg)",
              border: "1px solid var(--color-border)",
              color: "var(--color-text-light)",
              flexShrink: 0,
            }}
          >
            <FaChevronDown size={12} />
          </motion.span>
        </div>
      </div>

      {/* Collapsible lower section (kept mounted to preserve child state) */}
      <motion.div
        initial={false}
        animate={{ height: cardOpen ? "auto" : 0, opacity: cardOpen ? 1 : 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        style={{ overflow: "hidden" }}
      >
        {/* Collapsible sections */}
        <div style={{ marginBottom: 14 }}>
          {RESELL_BOXES.map((box) => {
            const category = findCategory(deal.categories, box);
            const { value } = summarizeBox(category, box);
            const items = category?.items || [];
            return (
              <CollapsibleSection
                key={box.key}
                icon={box.icon}
                accent={box.accent}
                label={box.label}
                count={value}
                unit={box.unit}
                open={!!openSections[box.key]}
                onToggle={() => toggleSection(box.key)}
              >
                <KeyValueList items={items} />
              </CollapsibleSection>
            );
          })}
        </div>

        {/* PMO row */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: avatarColor,
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {initials}
          </div>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                color: "var(--color-text-light)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              PMO
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: "var(--color-text)",
                wordBreak: "break-word",
              }}
            >
              {pmoEmail || (deal.client_name ? deal.client_name : "—")}
            </div>
          </div>
        </div>

        {/* Deal status / latest milestone (UI-only summary, derived from existing data) */}
        {dealStatusItem && (
          <div
            style={{
              marginTop: 14,
              display: "flex",
              gap: 14,
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
          <div style={{ minWidth: 110, display: "flex", flexDirection: "column", gap: 4 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--color-text-light)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              Deal Status
            </div>
            <div style={{ fontSize: 11, color: "var(--color-text-light)" }}>1 UPDATE</div>
          </div>

          <div
            style={{
              flex: 1,
              minWidth: 220,
              background: "var(--color-bg)",
              border: "1px solid var(--color-border)",
              borderRadius: 10,
              padding: "10px 12px",
              display: "flex",
              gap: 12,
              alignItems: "flex-start",
            }}
          >
            {deal.close_date && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  minWidth: 64,
                }}
              >
                <span
                  style={{
                    color: "#b45309",
                    fontWeight: 700,
                    fontSize: 13,
                    whiteSpace: "nowrap",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <FaCalendarAlt size={10} />
                  {new Date(deal.close_date).toLocaleDateString(undefined, {
                    month: "short",
                    year: "numeric",
                  })}
                </span>
              </div>
            )}
            <div
              style={{
                fontSize: 12.5,
                color: "var(--color-text)",
                lineHeight: 1.5,
                wordBreak: "break-word",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                flex: 1,
                minWidth: 0,
              }}
            >
              {dealStatusItem.value || "—"}
            </div>
          </div>
        </div>
        )}
      </motion.div>
    </motion.div>
  );
};

/* ────────────────────────── Main component ────────────────────────── */

export default function ResellCards({ deals }) {
  return (
    <div>
      {deals.map((deal, i) => (
        <ResellCard key={deal.deal_id ?? i} deal={deal} index={i} />
      ))}
    </div>
  );
}
